import { supabase } from '../supabase';
import { Database, Tables } from '@/types/database.types';
import { handleError, checkWorkspaceAccess } from './db-utils';
import { getProjectById } from './projects';

type DocumentRow = Tables<'documents'>;
export type DocumentType = Database['public']['Enums']['document_type'];

export type DocumentWithProject = DocumentRow & {
  project_name: string;
};

/**
 * Heuristic mapping from filename to document type. Used both by the
 * project create flow's lazy upload step and by the per-project Documents
 * tab's manual upload — kept in one place so the inference stays consistent.
 */
export function inferDocumentType(filename: string): DocumentType {
  const lower = filename.toLowerCase();
  if (lower.includes('meeting') || lower.includes('minutes')) return 'meeting_notes';
  if (lower.includes('email') || lower.includes('thread')) return 'email_thread';
  if (lower.includes('slack')) return 'slack_export';
  if (lower.includes('plan') || lower.includes('roadmap')) return 'project_plan';
  if (lower.includes('status') || lower.includes('report')) return 'status_report';
  return 'other';
}

/**
 * Get all documents for a workspace, joined with project name
 */
export const getWorkspaceDocuments = async (workspaceId: string): Promise<DocumentWithProject[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const hasAccess = await checkWorkspaceAccess(workspaceId);
    if (!hasAccess) throw new Error('Access denied to workspace');

    const { data, error } = await supabase
      .from('documents')
      .select(`
        *,
        projects!inner(name, workspace_id)
      `)
      .eq('projects.workspace_id', workspaceId)
      .order('uploaded_at', { ascending: false });

    if (error) handleError(error, 'fetch workspace documents');

    return (data || []).map(doc => {
      const project = doc.projects as unknown as { name: string; workspace_id: string };
      return {
        ...doc,
        projects: undefined,
        project_name: project.name,
      };
    }) as DocumentWithProject[];
  } catch (error) {
    handleError(error, 'fetch workspace documents');
    return [];
  }
};

/**
 * Get all documents for a single project
 */
export const getProjectDocuments = async (projectId: string): Promise<DocumentRow[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('project_id', projectId)
      .order('uploaded_at', { ascending: false });

    if (error) handleError(error, 'fetch project documents');
    return data || [];
  } catch (error) {
    handleError(error, 'fetch project documents');
    return [];
  }
};

/**
 * Upload a document to storage and insert a row in the documents table
 */
export const uploadDocument = async (
  projectId: string,
  file: File,
  documentType: 'meeting_notes' | 'email_thread' | 'slack_export' | 'project_plan' | 'status_report' | 'other'
): Promise<DocumentRow> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    const docId = crypto.randomUUID();
    const storagePath = `${projectId}/${docId}/${file.name}`;

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from('project-documents')
      .upload(storagePath, file);

    if (uploadError) handleError(uploadError, 'upload document file');

    // Insert document row
    const { data, error } = await supabase
      .from('documents')
      .insert({
        id: docId,
        project_id: projectId,
        name: file.name.replace(/\.[^/.]+$/, ''),
        original_name: file.name,
        type: documentType,
        file_path: storagePath,
        file_size: file.size,
        mime_type: file.type || null,
        uploaded_by: user.id,
      })
      .select()
      .single();

    if (error) {
      // Clean up storage on DB insert failure
      await supabase.storage.from('project-documents').remove([storagePath]);
      handleError(error, 'insert document record');
    }

    if (!data) throw new Error('No data returned from insert');
    return data;
  } catch (error) {
    handleError(error, 'upload document');
    throw error;
  }
};

/**
 * Delete a document from storage and the documents table
 */
export const deleteDocument = async (documentId: string): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get the document to find the file path
    const { data: doc, error: fetchError } = await supabase
      .from('documents')
      .select('*')
      .eq('id', documentId)
      .single();

    if (fetchError || !doc) handleError(fetchError || new Error('Document not found'), 'fetch document for deletion');

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('project-documents')
      .remove([doc!.file_path]);

    if (storageError) console.error('Failed to delete file from storage:', storageError);

    // Delete from database
    const { error } = await supabase
      .from('documents')
      .delete()
      .eq('id', documentId);

    if (error) handleError(error, 'delete document');
  } catch (error) {
    handleError(error, 'delete document');
    throw error;
  }
};

/**
 * Replace a document's file in storage, keeping the same document row
 */
export const replaceDocument = async (documentId: string, file: File): Promise<DocumentRow> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get existing document
    const { data: doc, error: fetchError } = await supabase
      .from('documents')
      .select('*')
      .eq('id', documentId)
      .single();

    if (fetchError || !doc) handleError(fetchError || new Error('Document not found'), 'fetch document for replacement');

    // Remove old file from storage
    await supabase.storage
      .from('project-documents')
      .remove([doc!.file_path]);

    // Upload new file
    const newPath = `${doc!.project_id}/${documentId}/${file.name}`;
    const { error: uploadError } = await supabase.storage
      .from('project-documents')
      .upload(newPath, file);

    if (uploadError) handleError(uploadError, 'upload replacement file');

    // Update document row
    const { data, error } = await supabase
      .from('documents')
      .update({
        file_path: newPath,
        file_size: file.size,
        mime_type: file.type || null,
        original_name: file.name,
        name: file.name.replace(/\.[^/.]+$/, ''),
      })
      .eq('id', documentId)
      .select()
      .single();

    if (error) handleError(error, 'update document record');
    if (!data) throw new Error('No data returned from update');
    return data;
  } catch (error) {
    handleError(error, 'replace document');
    throw error;
  }
};

/**
 * Get a signed download URL for a document
 */
export const getDocumentDownloadUrl = async (filePath: string): Promise<string> => {
  try {
    const { data, error } = await supabase.storage
      .from('project-documents')
      .createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) handleError(error, 'create signed URL');
    if (!data?.signedUrl) throw new Error('No signed URL returned');
    return data.signedUrl;
  } catch (error) {
    handleError(error, 'get document download URL');
    throw error;
  }
};
